import { AuthService } from "../AuthService/AuthService";
import { GetService } from "../DependencyInjection/DependencyInjection";
import { useObservableValue } from "../Observable/Observable";
import { useRef, useEffect, useState } from "react";
import './LoginWindow.css';

export const LoginWindow = () => {
    const authService = GetService(AuthService);
    const authStatus = useObservableValue(authService.authStatus);
    const authSession = useObservableValue(authService.session);
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const confirmPasswordRef = useRef<HTMLInputElement>(null);
    const [isLogin, setIsLogin] = useState(true);

    const [registerValidationError, setRegisterValidationError] = useState<string | null>(null);
    const [passwordValidationError, setPasswordValidationError] = useState<string | null>(null);
    const [registered, setRegistered] = useState(false);

    useEffect(() => {
        if(authStatus === "SIGNED_IN" || authStatus === "TOKEN_REFRESHED" || authStatus === "USER_UPDATED") {
            // User is authenticated, will be handled by parent component
        }
    }, [authStatus, authSession]);

    useEffect(() => {
        emailRef.current && (emailRef.current.value = "");
        passwordRef.current && (passwordRef.current.value = "");
        confirmPasswordRef.current && (confirmPasswordRef.current.value = "");
        setRegisterValidationError(null);
        setPasswordValidationError(null);
        setRegistered(false);
    }, [isLogin]);

    const validateRegister = () => {
        let passwordValid = true;
        let emailValid = true;

        if(passwordRef.current?.value !== confirmPasswordRef.current?.value) {
            setPasswordValidationError("Passwords do not match");
            passwordValid = false;
        }

        if(passwordRef.current?.value === "") {
            setPasswordValidationError("Password is required");
            passwordValid = false;
        }

        if(emailRef.current?.value === "") {
            setRegisterValidationError("Email is required");
            emailValid = false;
        }

        if(!emailRef.current?.value.endsWith("@cadprojekt.com.pl")) {
            setRegisterValidationError("Wrong email domain");
            emailValid = false;
        }

        if(passwordValid) setPasswordValidationError(null);
        if(emailValid) setRegisterValidationError(null);
        
        if(passwordValid && emailValid) {
            setRegistered(true);
            GetService(AuthService).signUp(emailRef.current?.value!, passwordRef.current?.value!);
        }
    }
    
    return (
        <div className="login-container">
            <div className="login-card">
                {isLogin ? (
                    <form className="login-form" onSubmit={(e) => {
                        e.preventDefault();
                        GetService(AuthService).signIn(emailRef.current?.value!, passwordRef.current?.value!);
                    }}>
                        {registered && <p className="success-message">A verification email has been sent</p>}
                        <h2 className="login-title">Log into your account</h2>
                        
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input 
                                type="email" 
                                id="email"
                                ref={emailRef}
                                className="form-input"
                                placeholder="Enter your email"
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <input 
                                type="password" 
                                id="password"
                                ref={passwordRef}
                                className="form-input"
                                placeholder="Enter your password"
                                required
                            />
                        </div>
                        
                        <button type="submit" className="btn btn-primary">
                            Log in
                        </button>
                        
                        <div className="form-footer">
                            <p>Don't have an account?</p>
                            <button 
                                type="button" 
                                className="btn-link"
                                onClick={() => setIsLogin(false)}
                            >
                                Sign up
                            </button>
                        </div>
                    </form>
                ) : (
                    <form className="login-form" onSubmit={(e) => {
                        e.preventDefault();
                    }}>
                        <h2 className="login-title">Create an account</h2>
                        
                        <div className="form-group">
                            <label htmlFor="email-register">Email</label>
                            <input 
                                type="email" 
                                id="email-register"
                                ref={emailRef}
                                className={`form-input ${registerValidationError ? 'error' : ''}`}
                                placeholder="Enter your email"
                                required
                            />
                            {registerValidationError && (
                                <span className="error-message">{registerValidationError}</span>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="password-register">Password</label>
                            <input 
                                type="password" 
                                id="password-register"
                                ref={passwordRef}
                                className={`form-input ${passwordValidationError ? 'error' : ''}`}
                                placeholder="Enter your password"
                                required
                            />
                            {passwordValidationError && (
                                <span className="error-message">{passwordValidationError}</span>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="confirm-password">Confirm Password</label>
                            <input 
                                type="password" 
                                id="confirm-password"
                                ref={confirmPasswordRef}
                                className="form-input"
                                placeholder="Confirm your password"
                                required
                            />
                        </div>
                        
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            onClick={validateRegister}
                        >
                            Sign up
                        </button>
                        
                        <div className="form-footer">
                            <p>Already have an account?</p>
                            <button 
                                type="button" 
                                className="btn-link"
                                onClick={() => setIsLogin(true)}
                            >
                                Log in
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
